import { GetApiBaseUrl } from 'src/constants/UrlConstants';
import { UploaderConstants } from 'src/constants/ProjectConstants';
import {
  CREATE_DOCUMENT_S3_URL,
  GET_UPLOAD_S3_URL,
  UPLOAD_DMS_DOCUMENT_TO_S3,
} from 'src/constants/ApiConstants';
import { IDmsFile } from './DmsModel';
import { logger } from 'src/logger';
import { fetchWithRetryAndCaptureMetrics } from 'src/utilities/ApiHelper';
import { PAGE_SCOPE } from 'src/constants/GlobalConstants';

const DMS_S3_UPLOAD_SUCCESS = 'DMS_S3_UPLOAD_SUCCESS';
export default class DmsFileUploadClient {
  private file: File;
  private pageScope: PAGE_SCOPE;

  constructor(file: File, pageScope: PAGE_SCOPE) {
    this.file = file;
    this.pageScope = pageScope;
  }

  public orchestrateFileUpload = async (): Promise<IDmsFile> => {
    try {
      const presignedUrl = await this.generatePresignedUrl();
      logger.info(
        `Generated presigned URL for file: ${this.file.name} is: ${presignedUrl}`,
      );
      await this.uploadDocumentToS3(presignedUrl, this.file);
      logger.info(`Successfully uploaded document ${this.file.name} to S3`);
      const uploadedDocumentObject = await this.invokeCreateDocument(
        presignedUrl,
        this.file,
      );
      logger.info(`Document is created in DMS. Check response in context: `, {
        uploadedDocumentObject,
      });

      return uploadedDocumentObject;
    } catch (error) {
      logger.error(
        `${UploaderConstants.ERROR_UPLOAD_TO_DMS} for file ${
          this.file.name
        }. Reason:  ${JSON.stringify(error)}`,
      );
      throw error;
    }
  };

  private invokeCreateDocument = async (
    s3Url: string,
    file: File,
  ): Promise<IDmsFile> => {
    try {
      const createDocumentUri =
        this.pageScope === PAGE_SCOPE.PUBLIC
          ? CREATE_DOCUMENT_S3_URL.getPublicRoute()
          : CREATE_DOCUMENT_S3_URL.getRoute();
      const response = await fetchWithRetryAndCaptureMetrics(
        CREATE_DOCUMENT_S3_URL.metricsPublisher,
        `${GetApiBaseUrl()}${createDocumentUri}`,
        {
          method: CREATE_DOCUMENT_S3_URL.method,
          body: JSON.stringify({
            s3Url: s3Url,
            type: 'CPRRegulatorDocument',
            fileExtension: file.name?.split('.').pop(),
            fileName: file.name,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.status.startsWith('2') && response.body) {
        return response.body;
      }
      return Promise.reject(
        UploaderConstants.ERROR_UNABLE_TO_TRIGGER_CREATE_DOCUMENT,
      );
    } catch (error) {
      logger.error(
        `invokeCreateDocument failed. Details ${JSON.stringify(error)}`,
      );
      throw error;
    }
  };

  private uploadDocumentToS3 = async (
    s3Url: string,
    file: File,
  ): Promise<string> => {
    try {
      const response = await fetchWithRetryAndCaptureMetrics(
        UPLOAD_DMS_DOCUMENT_TO_S3.metricsPublisher,
        s3Url,
        {
          method: UPLOAD_DMS_DOCUMENT_TO_S3.method,
          headers: {
            'Content-Type': 'binary/octet-stream',
          },
          body: file,
        },
      );
      //Response is 2XX
      if (response.status.startsWith('2')) {
        return DMS_S3_UPLOAD_SUCCESS;
      }
      return Promise.reject(
        UploaderConstants.ERROR_UNABLE_TO_UPLOAD_FILE_TO_DMS_S3,
      );
    } catch (error) {
      logger.error(
        `uploadDocumentToS3 failed. Details ${JSON.stringify(error)}`,
      );
      throw error;
    }
  };

  private generatePresignedUrl = async (): Promise<string> => {
    try {
      const generatePresignedUri =
        this.pageScope === PAGE_SCOPE.PUBLIC
          ? GET_UPLOAD_S3_URL.getPublicRoute()
          : GET_UPLOAD_S3_URL.getRoute();
      const response = await fetchWithRetryAndCaptureMetrics(
        GET_UPLOAD_S3_URL.metricsPublisher,
        `${GetApiBaseUrl()}${generatePresignedUri}`,
        {
          method: GET_UPLOAD_S3_URL.method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        },
      );
      const responseJson = response.body;
      const s3Url = responseJson?.s3Url;
      if (s3Url) {
        return s3Url;
      }
      return Promise.reject(UploaderConstants.ERROR_EMPTY_S3_URL);
    } catch (error) {
      logger.error(
        `generatePresignedUrl failed. Details ${JSON.stringify(error)}`,
      );
      throw error;
    }
  };
}
